import React, { useMemo } from 'react';

import GoogleMapReact from 'google-map-react';
import { Box, Tooltip, Typography } from '@mui/material';

import {
  AdminLegendVariant,
  getNearbyProps,
  ICalendarEventType,
  PharmacistLegendVariant,
  setNearbyProps,
} from '@pharmaplan/common';

import useUsersLocation, { Position } from '../../../../hooks/useUsersLocation';
import useWindowDimensions from '../../../../hooks/useWindowDimensions';
import { createOffset } from '../../../Dashboard/PharmacistMap/util';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import ProfessionalMapMarkers from '../../ProfessionalMapMarkers';
import useGetItems from '../../../../hooks/useGetItems';
import useAdmin from '../../../../hooks/useAdmin';
import useMap from '../../../../hooks/Map/useMap';

import classes from './style';
import './style.scss';
import AdminMapTooltip from './AdminMapTooltip';

const defaultProps = {
  center: {
    lat: 45.5019,
    lng: -73.5674,
  },
  zoom: 12,
};

const offsetCoords = (
  type: ICalendarEventType,
  lat: number,
  lng: number,
): { lat: number; lng: number } => {
  switch (type) {
    case PharmacistLegendVariant.booked:
      return { lat, lng };
    case PharmacistLegendVariant.requested:
      return createOffset({ latitude: lat, longitude: lng }, 3, 3);
    case AdminLegendVariant.posted:
      return createOffset({ latitude: lat, longitude: lng }, 1, 1);

    default:
      return { lat: -91, lng: -181 }; // WILL NOT SHOW ON MAP
  }
};

const Marker = ({
  type,
  text,
  onClick,
  banner,
  name,
  pharmacyId,
  showAlert,
}: any) =>
  (
    <Tooltip
      title={
        <AdminMapTooltip banner={banner} name={name} pharmacyId={pharmacyId} />
    }
    >
      <Box sx={classes.marker(type)} onClick={onClick}>
        {showAlert && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            height: 10,
            width: 10,
            borderRadius: 5,
            backgroundColor: 'common.counterRed',
          }}
        />
        )}
        <Typography sx={classes.markerText}>{text}</Typography>
      </Box>
    </Tooltip>
  );

const AdminMap = () => {
  const dispatch = useAppDispatch();
  const { height = 0 } = useWindowDimensions();
  const { getImage } = useGetItems();
  const { isAdmin } = useAdmin();

  const nearbyProps = useAppSelector(getNearbyProps);

  const {
    mapEvents,
    handleMapNavigation,
    professionals,
    handleProfessionalClick,
  } = useMap();

  const locationSuccess = (position: Position) => {
    const { coords } = position;
    const { latitude, longitude } = coords ?? {};

    dispatch(
      setNearbyProps({
        ...nearbyProps,
        latitude,
        longitude,
      }),
    );
  };

  useUsersLocation(locationSuccess);

  const filteredEvents = useMemo(
    () =>
      mapEvents?.filter((item) =>
        item?.latitude !== null),
    [mapEvents],
  );

  const uniqueCoords = new Set<string>([]);

  const mapNavigation = (type: ICalendarEventType, eventIds: Array<string>, pharmacyId?: string, isAutoSyncRecord?:boolean) =>
    () =>
      handleMapNavigation(type, eventIds, pharmacyId, isAutoSyncRecord);

  return (
    <div style={{ minHeight: 550, height: (height * 3) / 4, width: '100%' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env?.REACT_APP_GEO_KEY as string }}
        defaultCenter={defaultProps.center}
        center={{ lat: nearbyProps.latitude, lng: nearbyProps.longitude }}
        defaultZoom={defaultProps.zoom}
      >
        {filteredEvents.map((item) => {
          let found = 0;

          let coords = {
            lat: Number(item.latitude),
            lng: Number(item.longitude),
          };

          const parsed = JSON.stringify({
            lat: item.latitude,
            long: item.longitude,
          });

          const isAlreadyPresent = uniqueCoords.has(parsed);

          if (isAlreadyPresent) {
            found += 1;
            coords = createOffset(
              {
                latitude: Number(item.latitude),
                longitude: Number(item.longitude),
              },
              found,
              found,
            );
          } else {
            uniqueCoords.add(parsed);
          }

          return item.events.map((event) => {
            const { count, type, data } = event ?? {};
            const { lat, lng } = offsetCoords(type, coords.lat, coords.lng);

            return (
              <Marker
                key={event.data[0].id}
                showAlert={isAlreadyPresent}
                lat={lat}
                lng={lng}
                text={count}
                type={type}
                name={item.pharmacyName}
                pharmacyId={item.pharmacyId}
                banner={getImage(item.logoId)}
                onClick={mapNavigation(
                  type,
                  data.map((ev) =>
                    ev.id),
                  item?.pharmacyId,
                  item?.isAutoSyncRecord,
                )}
              />
            );
          });
        })}

        {isAdmin
          && professionals.map((item) => {
            const { latitude, longitude, professionalId, userType, name } = item ?? {};
            let found = 0;

            let coords = {
              lat: Number(latitude),
              lng: Number(longitude),
            };

            const parsed = JSON.stringify({
              lat: latitude,
              long: longitude,
            });

            const isAlreadyPresent = uniqueCoords.has(parsed);

            if (isAlreadyPresent) {
              found += 1;
              coords = createOffset(
                {
                  latitude: Number(item.latitude),
                  longitude: Number(item.longitude),
                },
                found,
                found,
              );
            } else {
              uniqueCoords.add(parsed);
            }

            return (
              <ProfessionalMapMarkers
                handleClick={handleProfessionalClick(professionalId)}
                type={userType}
                name={name}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error: MAP INJECTS PROPS
                lat={coords.lat || null}
                lng={coords.lng || null}
              />
            );
          })}
      </GoogleMapReact>
    </div>
  );
};

export default AdminMap;
